import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, ArrowLeft, AlertTriangle, Info, FileSpreadsheet, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSalesInvoicingModuleStatus } from '../hooks/useSalesInvoicingModuleStatus';
import { SalesInvoicingPausedNotice } from '../components/sales-invoicing/SalesInvoicingPausedNotice';

const API = getBackendUrl();

const fmt = (v) =>
  typeof v === 'number'
    ? `£${v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    : '—';

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enabled, loading: moduleStatusLoading, pausedMessage } = useSalesInvoicingModuleStatus();

  const [lastInvoiceNo, setLastInvoiceNo] = useState('');
  const [fromDate,      setFromDate]      = useState('');
  const [toDate,        setToDate]        = useState('');
  const [jobScope,      setJobScope]      = useState('completed');

  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState('');

  const canGenerate =
    lastInvoiceNo !== '' &&
    !isNaN(parseInt(lastInvoiceNo, 10)) &&
    parseInt(lastInvoiceNo, 10) >= 0 &&
    fromDate &&
    toDate &&
    fromDate <= toDate;

  const generate = async () => {
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/reports/invoice-list`, {
        headers: authHeaders(),
        params: {
          from_date:            fromDate,
          to_date:              toDate,
          last_invoice_number:  parseInt(lastInvoiceNo, 10),
          job_scope:            jobScope,
        },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/api/reports/invoice-list/export`, {
        headers: authHeaders(),
        params: {
          from_date:            fromDate,
          to_date:              toDate,
          last_invoice_number:  parseInt(lastInvoiceNo, 10),
          job_scope:            jobScope,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a   = document.createElement('a');
      a.href    = url;
      a.download = `invoice_list_${fromDate}_${toDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed — please try again.');
    } finally {
      setExporting(false);
    }
  };

  const warnByType = (type) =>
    (result?.warnings || []).filter((w) => w.type === type);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/reports')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm transition-colors"
          data-testid="invoice-back-btn"
        >
          <ArrowLeft size={16} /> Reports
        </button>
        <div className="h-4 w-px bg-slate-300" />
        <h1 className="text-lg font-semibold text-slate-800">Invoice List</h1>
        <span className="ml-2 text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
          Financial &amp; Forecasting
        </span>
      </div>

      {moduleStatusLoading ? null : !enabled ? (
        <div className="max-w-6xl mx-auto px-6 py-8">
          <SalesInvoicingPausedNotice pausedMessage={pausedMessage} />
        </div>
      ) : (
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Parameters card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">
            Report Parameters
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Last Invoice Number */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Last Invoice Number <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 1042"
                value={lastInvoiceNo}
                onChange={(e) => setLastInvoiceNo(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                data-testid="invoice-last-number"
              />
              <p className="text-xs text-slate-400 mt-1">
                First invoice will be this&nbsp;+&nbsp;1. Same inputs always produce the same sequence.
              </p>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                data-testid="invoice-from-date"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                To Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                data-testid="invoice-to-date"
              />
            </div>

            {/* Job Scope */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Job Scope
              </label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setJobScope('completed')}
                  className={`flex-1 py-2 rounded text-xs font-medium border transition-colors ${
                    jobScope === 'completed'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'
                  }`}
                  data-testid="invoice-scope-completed"
                >
                  Completed
                </button>
                <button
                  onClick={() => setJobScope('all')}
                  className={`flex-1 py-2 rounded text-xs font-medium border transition-colors ${
                    jobScope === 'all'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'
                  }`}
                  data-testid="invoice-scope-all"
                >
                  All Jobs
                </button>
              </div>
            </div>
          </div>

          {/* VAT note */}
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded px-3 py-2 border border-slate-200">
            <Info size={13} className="shrink-0 text-emerald-600" />
            <span>20% UK VAT applied to each row. VAT and Total columns use Excel formulas in the exported workbook.</span>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={generate}
              disabled={!canGenerate || loading}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded transition-colors"
              data-testid="invoice-generate-btn"
            >
              {loading ? 'Generating…' : 'Generate'}
            </button>
            {result && (
              <button
                onClick={exportXlsx}
                disabled={exporting}
                className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-700 text-slate-700 text-sm font-medium rounded transition-colors"
                data-testid="invoice-export-btn"
              >
                <FileSpreadsheet size={15} />
                {exporting ? 'Exporting…' : 'Export .xlsx'}
              </button>
            )}
          </div>

          {error && (
            <p className="mt-3 text-sm text-red-600" data-testid="invoice-error">
              {error}
            </p>
          )}
        </div>

        {/* Warnings */}
        {result && (result.warnings || []).length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="invoice-warnings">
            <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold text-sm">
              <AlertTriangle size={16} /> Billing Data Warnings
            </div>
            {warnByType('missing_price').length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-medium text-amber-700 mb-1">Missing / zero price ({warnByType('missing_price').length} row{warnByType('missing_price').length > 1 ? 's' : ''}):</p>
                <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">
                  {warnByType('missing_price').map((w) => (
                    <li key={`p-${w.invoice_number}`}>Invoice #{w.invoice_number} — {w.hotel_name}</li>
                  ))}
                </ul>
              </div>
            )}
            {warnByType('missing_email').length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-700 mb-1">Missing email contact ({warnByType('missing_email').length} row{warnByType('missing_email').length > 1 ? 's' : ''}):</p>
                <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">
                  {warnByType('missing_email').map((w) => (
                    <li key={`e-${w.invoice_number}`}>Invoice #{w.invoice_number} — {w.hotel_name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="invoice-results">
            {/* Summary bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50 text-xs text-slate-600">
              <span>
                {result.totals.row_count} invoice{result.totals.row_count !== 1 ? 's' : ''}
                &nbsp;·&nbsp;
                {result.from_date} → {result.to_date}
                &nbsp;·&nbsp;
                Scope: <strong className="text-slate-700">{result.job_scope === 'completed' ? 'Completed' : 'All Jobs'}</strong>
                &nbsp;·&nbsp;
                VAT rate: <strong className="text-slate-700">{result.vat_rate}</strong>
                {!result.cellar_ledger_service_found && (
                  <span className="ml-2 text-amber-600 font-medium">
                    (Cellar Ledger service not configured — all rows show NO)
                  </span>
                )}
              </span>
              <div className="flex gap-4">
                <span>Net: <strong className="text-slate-800">{fmt(result.totals.net)}</strong></span>
                <span>VAT: <strong className="text-slate-800">{fmt(result.totals.vat)}</strong></span>
                <span>Total: <strong className="text-emerald-700">{fmt(result.totals.total_inc_vat)}</strong></span>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoice-table">
                <thead>
                  <tr className="bg-slate-800 text-white text-xs">
                    <th className="px-3 py-2.5 text-right font-semibold">INVOICE NUMBER</th>
                    <th className="px-3 py-2.5 text-left font-semibold">HOTEL NAME</th>
                    <th className="px-3 py-2.5 text-center font-semibold">DATE OF AUDIT</th>
                    <th className="px-3 py-2.5 text-right font-semibold">NET PRICE</th>
                    <th className="px-3 py-2.5 text-right font-semibold">VAT AMOUNT</th>
                    <th className="px-3 py-2.5 text-right font-semibold">TOTAL INVOICE INC VAT</th>
                    <th className="px-3 py-2.5 text-center font-semibold">CELLAR LEDGER</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Email Contact</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Auditors</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-slate-400 text-sm">
                        No jobs found for the selected date range and scope.
                      </td>
                    </tr>
                  ) : (
                    result.rows.map((row, i) => {
                      const hasPriceWarn = row.net_price === 0;
                      const hasEmailWarn = !row.email_contact;
                      return (
                        <tr
                          key={row.invoice_number}
                          className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                            hasPriceWarn ? 'bg-amber-50' : ''
                          }`}
                          data-testid={`invoice-row-${row.invoice_number}`}
                        >
                          <td className="px-3 py-2 text-right font-mono text-slate-700">
                            {row.invoice_number}
                          </td>
                          <td className="px-3 py-2 text-slate-800 font-medium">
                            {row.hotel_name}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">
                            {row.date_of_audit}
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${hasPriceWarn ? 'text-amber-600 font-semibold' : 'text-slate-700'}`}>
                            {fmt(row.net_price)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-slate-600">
                            {fmt(row.vat_amount)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">
                            {fmt(row.total_inc_vat)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              row.cellar_ledger === 'YES'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                              {row.cellar_ledger}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-slate-600 text-xs ${hasEmailWarn ? 'text-amber-500 italic' : ''}`}>
                            {row.email_contact || '—'}
                          </td>
                          <td className="px-3 py-2 text-slate-600 text-xs" data-testid={`invoice-auditors-${row.invoice_number}`}>
                            {row.auditor_names || <span className="text-slate-400">Unassigned</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {/* Grand total row */}
                {(result.rows || []).length > 0 && (
                  <tfoot>
                    <tr className="bg-emerald-700 text-white font-semibold text-sm" data-testid="invoice-totals-row">
                      <td className="px-3 py-2.5 text-right" colSpan={3}>Grand Total</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt(result.totals.net)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt(result.totals.vat)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt(result.totals.total_inc_vat)}</td>
                      <td className="px-3 py-2.5 text-center"></td>
                      <td className="px-3 py-2.5"></td>
                      <td className="px-3 py-2.5"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
